import { instagram, OrderingValues } from '@round/api';
import { SkeletonTableCell } from '@round/ui-kit';
import { ColumnDef } from '@tanstack/react-table';
import { numberWithCommas } from 'helpers';
import moment from 'moment';
import { useState } from 'react';
import { TruncatedLinkCell, LinkData } from '@round/whitelabel-ui';
import { PaginationTable } from '@round/whitelabel-ui';
import { useTopPosts } from './useTopPosts';

type Props = {
    audioId: number;
};

export type TableRow = instagram.InstagramPost;

export type Meta = {
    isLoading: boolean;
};

const TopPosts = ({ audioId }: Props) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [ordering, setOrdering] = useState<OrderingValues<instagram.GetInstagramPostsSortableKeys> | undefined>(
        '-view_count'
    );

    const { rows, meta, count, status } = useTopPosts({
        page,
        page_size: pageSize,
        ordering,
        audio_id: audioId?.toString(),
    });

    const columns: ColumnDef<TableRow, any>[] = [
        {
            header: 'Username',
            accessorFn: (row) => row.owner_username || '-',
            cell: SkeletonTableCell,
        },
        {
            header: 'Followers',
            accessorFn: (row) => (row.owner_follower_count ? numberWithCommas(row.owner_follower_count) : '-'),
            cell: SkeletonTableCell,
        },
        {
            header: 'Location',
            accessorFn: (row) => row.location || '-',
            cell: SkeletonTableCell,
        },
        {
            header: 'Description',
            accessorFn: (row): LinkData => ({
                href: row.post_url,
                value: row.text || '-',
            }),
            cell: TruncatedLinkCell,
        },
        {
            header: 'Date Posted',
            id: 'taken_at',
            enableSorting: true,
            accessorFn: (row) => (row.taken_at ? moment(row.taken_at).format('D MMM YY') : '-'),
            cell: SkeletonTableCell,
        },
        {
            header: 'Plays',
            enableSorting: true,
            id: 'play_count',
            accessorFn: (row) => (row.play_count ? numberWithCommas(row.play_count) : '-'),
            cell: SkeletonTableCell,
        },
        {
            header: 'Views',
            enableSorting: true,
            id: 'view_count',
            accessorFn: (row) => (row.view_count ? numberWithCommas(row.view_count) : '-'),
            cell: SkeletonTableCell,
        },
        {
            header: 'Likes',
            accessorFn: (row) => (row.like_count ? numberWithCommas(row.like_count) : '-'),
            cell: SkeletonTableCell,
        },
        {
            header: 'Comments',
            accessorFn: (row) => (row.comment_count ? numberWithCommas(row.comment_count) : '-'),
            cell: SkeletonTableCell,
        },
    ];

    return (
        <PaginationTable
            data={rows}
            columns={columns}
            ordering={ordering}
            onOrderingChange={(ordering) => setOrdering(ordering[0] || undefined)}
            page={page}
            count={count ?? 0}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            meta={meta}
            hasError={status === 'error'}
        />
    );
};

export default TopPosts;
